import moment from 'moment';
import {
  required as vRequired,
  helpers,
  email as vEmail,
  not as vNot,
  url as vUrl,
  minLength as vMinLength,
  maxLength as vMaxLength,
  minValue as vMinValue,
  maxValue as vMaxValue,
  numeric as vNumeric,
} from 'vuelidate/lib/validators';

// const customExampleValidatorWithMetaData = (param) =>
//   helpers.withParams({errorMessage: 'Error Message!'}, (value) => {
//     if (helpers.req(param)) {
//       // do something useful with "value" to be validated and param(s)
//       // access "errorMessage via $params.[nameOfGivenFieldInConfig].errorMessage"
//       return true;
//     }
//     return false;
//   });

export const countryCodes = '(AF|AX|AL|DZ|AS|AD|AO|AI|AQ|AG|AR|AM|AW|AU|AT|AZ|BS|BH|BD|BB|BY|BE|BZ|BJ|BM|BT|BO|'
  + 'BQ|BA|BW|BV|BR|IO|BN|BG|BF|BI|KH|CM|CA|CV|KY|CF|TD|CL|CN|CX|CC|CO|KM|CG|CD|CK|CR|CI|HR|CU|CW|CY|CZ|DK|DJ|DM|'
  + 'DO|EC|EG|SV|GQ|ER|EE|ET|FK|FO|FJ|FI|FR|GF|PF|TF|GA|GM|GE|DE|GH|GI|GR|GL|GD|GP|GU|GT|GG|GN|GW|GY|HT|HM|VA|HN|'
  + 'HK|HU|IS|IN|ID|IR|IQ|IE|IM|IL|IT|JM|JP|JE|JO|KZ|KE|KI|KP|KR|KW|KG|LA|LV|LB|LS|LR|LY|LI|LT|LU|MO|MK|MG|MW|MY|'
  + 'MV|ML|MT|MH|MQ|MR|MU|YT|MX|FM|MD|MC|MN|ME|MS|MA|MZ|MM|NA|NR|NP|NL|NC|NZ|NI|NE|NG|NU|NF|MP|NO|OM|PK|PW|PS|PA|'
  + 'PG|PY|PE|PH|PN|PL|PT|PR|QA|RE|RO|RU|RW|BL|SH|KN|LC|MF|PM|VC|WS|SM|ST|SA|SN|RS|SC|SL|SG|SX|SK|SI|SB|SO|ZA|GS|'
  + 'SS|ES|LK|SD|SR|SJ|SZ|SE|CH|SY|TW|TJ|TZ|TH|TL|TG|TK|TO|TT|TN|TR|TM|TC|TV|UG|UA|AE|GB|US|UM|UY|UZ|VU|VE|VN|VG|'
  + 'VI|WF|EH|YE|ZM|ZW)';

export const generateValidatorWithParams = (
  errorMessage,
  validatorFunc,
  validateOnlyWhenValueSet = true,
  negateCondition = false,
) => (params = {}, getModel) => {
  if (params instanceof Function) {
    const condition = params;
    params = {
      evaluateCondition: () => {
        const model = getModel && getModel();
        return condition(model);
      },
    };
  }

  if (params.field) {
    params.fields = [params.field];
  }

  if (getModel) {
    if (params.condition) {
      params.evaluateCondition = () => {
        const evaluated = params.condition(...params.fields.map(field => getModel()[field]));
        return negateCondition ? !evaluated : evaluated;
      };
    } else if (params.field && params.value) {
      params.evaluateCondition = () => {
        const evaluated = valueAsList(params.value).includes(getModel()[params.field]);
        return negateCondition ? !evaluated : evaluated;
      };
    }
  }

  return helpers.withParams({ errorMessage, params }, (value, vm) => {
    if (validateOnlyWhenValueSet) {
      if (value || value === 0) {
        return validatorFunc(value, vm, params) || false;
      }
      return true;
    }
    return validatorFunc(value, vm, params) || false;
  });
};

export const generateValidator = (errorMessage, validatorFunc, validateOnlyWhenValueSet = true) => generateValidatorWithParams(
  errorMessage,
  validatorFunc,
  validateOnlyWhenValueSet,
)();

export const required = generateValidator(
  'Dieses Feld wird benötigt',
  value => vRequired(value),
  false,
);

export const numeric = generateValidator(
  'Darf nur Ziffern enthalten',
  value => vNumeric(value),
);

export const phoneNumber = generateValidator(
  'Keine gültige Telefonnummer (Ziffern sowie Klammern, Schrägstriche und Pluszeichen sind erlaubt)',
  value => vNumeric(value.replace(/[\s+()/]/g, '')),
);

export const fullname = generateValidator(
  'Kein gültiger Name',
  value => value.length > 6 && value.split(' ').length > 1,
);

export const email = generateValidator(
  'Falsche Email-Adresse',
  value => vEmail(value),
);

export const notEmail = generateValidator(
  'Darf keine E-Mail-Adresse enthalten',
  value => vNot(vEmail)(value),
);

export const notUrl = generateValidator(
  'Darf keine URL enthalten',
  value => vNot(vUrl)(value),
);

export const url = generateValidator(
  'Muss eine URL enthalten',
  value => vUrl(value),
);

export const startsWith = txt => generateValidator(
  `Muss mit "${txt}" anfangen`,
  value => value.startsWith(txt),
);

export const notFutureDate = generateValidator(
  'Datum darf nicht in der Zukunft liegen',
  value => new Date(value) <= new Date(),
);

export const lengthEquals = length => generateValidator(
  `Eingabe muss exakt ${length} Zeichen lang sein.`,
  value => value && value.length === length,
);

export const mustSatisfyIfConditionSatisifed = message => generateValidatorWithParams(
  message,
  vmValidator,
);

export const mustSatisfyIfFieldEquals = message => generateValidatorWithParams(
  message,
  (value, vm, params) => vmValidator(value, vm, {
    fields: [params.field],
    condition: fieldValue => valueAsList(params.value).includes(fieldValue),
    validation: params.validation,
  }),
);

export const emptyIfFieldEquals = message => generateValidatorWithParams(
  message,
  (value, vm, params) => vmValidator(value, vm, {
    fields: [params.field],
    condition: fieldValue => valueAsList(params.value).includes(fieldValue),
    validation: vNot(vRequired),
  }),
);

export const requiredIfConditionSatisfied = generateValidatorWithParams(
  'Dieses Feld wird benötigt',
  (value, vm, params) => vmValidator(value, vm, { ...params, validation: vRequired }),
  false,
);

export const requiredIfFieldEquals = generateValidatorWithParams(
  'Dieses Feld wird benötigt',
  (value, vm, params) => vmValidator(value, vm, {
    fields: [params.field],
    condition: fieldValue => valueAsList(params.value).includes(fieldValue),
    validation: vRequired,
  }),
  false,
);

export const requiredIfFieldNotEquals = generateValidatorWithParams(
  'Dieses Feld wird benötigt',
  (value, vm, params) => vmValidator(value, vm, {
    fields: [params.field],
    condition: fieldValue => !valueAsList(params.value).includes(fieldValue),
    validation: vRequired,
  }),
  false,
  true,
);

export const requiredIf = generateValidatorWithParams(
  'Dieses Feld wird benötigt',
  (value, vm, params) => {
    const conditionEvaluation = params.evaluateCondition(vm);
    return (conditionEvaluation && vRequired(value)) || !conditionEvaluation;
  },
  false,
);

function vmValidator(value, vm, params) {
  const fieldValues = params.fields.map(field => vm[field]);
  const conditionEvaluation = params.condition(...fieldValues);
  return (conditionEvaluation && params.validation(value)) || !conditionEvaluation;
}

function valueAsList(value) {
  return Array.isArray(value) ? value : [value];
}

export const maxLength = length => generateValidator(
  `Dieses Feld darf höchstens ${length} Zeichen lang sein`,
  value => vMaxLength(length)(value),
);

export const minLength = length => generateValidator(
  `Dieses Feld muss mindestens ${length} Zeichen lang sein`,
  value => vMinLength(length)(value),
);

export const iban = generateValidator(
  'Eine IBAN muss exakt 20 Zeichen lang sein',
  value => value && value.replace(/ /g, '').length === 20,
);

export const appendMetaData = metadata => helpers.withParams(metadata, () => true);

export const origin = originString => appendMetaData({ origin: originString });

const vDate = (options = {}) => (value) => {
  if (!value) return true;
  if (value === 'Invalid date') return false;

  const date = moment(value);
  if (!date.isValid() || date.toISOString() !== value) return false;

  if (!options.min && !options.max) return true;

  const min = options.min
    ? moment(options.min).startOf('day')
    : null;

  const max = options.max
    ? moment(options.max)
    : null;

  if (min && !date.isSameOrAfter(min)) return false;
  if (max && date.isAfter(max)) return false;

  return true;
};

export const isDate = (format = 'TT.MM.JJJJ') => generateValidator(
  `Bitte geben Sie ein gültiges Datum im Format ${format} ein`,
  value => vDate()(value),
);

export const isDateAfter = (getMinDate, format = 'DD.MM.YYYY') => generateValidator(
  function () {
    // 'this' is the Vue instance
    const minDate = typeof getMinDate === 'function' ? getMinDate.call(this) : getMinDate;
    return `Das Datum muss nach dem "${moment(minDate).format(format)}" sein`;
  },
  function (value) {
    const minDate = typeof getMinDate === 'function' ? getMinDate.call(this) : getMinDate;
    return vDate({ min: minDate })(value);
  },
);

export const isDateBefore = (date, format = 'DD.MM.YYYY') => generateValidator(
  `Das Datum muss vor dem "${moment(date).format(format)}" sein`,
  value => vDate({ max: date })(value),
);

export const isHigherThan = (minValue, currency = '€') => generateValidator(
  `Der Wert muss über ${minValue}${currency} liegen`,
  value => vMinValue(minValue)(value),
);

export const isLowerThan = (maxValue, currency = '€') => generateValidator(
  `Der Wert darf maximal bei ${maxValue}${currency} liegen`,
  value => vMaxValue(maxValue)(value),
);

export const customValidator = (message, validatorFn) => generateValidator(
  message,
  validatorFn,
);
